import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import apis from "../../services";
import { Box, Container } from "@mui/material";
import AdminNewSidebar from "components/admin/AdminNewSidebar";
import { Header } from "components";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import DOMPurify from "dompurify";

const ViewPopup = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [popupData, setPopupData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPopupData = async () => {
      try {
        const response = await apis.getPopupbyId(id);
        if (response?.data?.status) {
          setPopupData(response.data.popup);
        } else {
          toast.error("Failed to fetch popup details");
        }
      } catch (error) {
        toast.error(error.response?.data?.message || "Error fetching popup");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchPopupData();
    }
  }, [id]);

  if (loading) {
    return (
      <Box className="dashboard-main">
        <AdminNewSidebar />
        <div className="dashboard-section-main">
          <Box
            component="main"
            sx={{ flexGrow: 1, height: "100vh", overflow: "auto" }}
            className="custom-scroll"
          >
            <Header />
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
            </div>
          </Box>
        </div>
      </Box>
    );
  }

  if (!popupData) {
    return (
      <Box className="dashboard-main">
        <AdminNewSidebar />
        <div className="dashboard-section-main">
          <Box
            component="main"
            sx={{ flexGrow: 1, height: "100vh", overflow: "auto" }}
            className="custom-scroll"
          >
            <Header />
            <div className="text-center text-red-500 py-10 text-xl font-medium">
              Popup not found.
            </div>
          </Box>
        </div>
      </Box>
    );
  }

  return (
    <Box className="dashboard-main">
      <AdminNewSidebar />
      <div className="dashboard-section-main">
        <Box
          component="main"
          sx={{ flexGrow: 1, height: "100vh", overflow: "auto" }}
          className="custom-scroll"
        >
          <Header />
          <Container
            maxWidth="lg"
            sx={{ mt: 4, mb: 4 }}
            className="mainContainerComp"
          >
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "20px",
              }}
            >
              <motion.h2
                initial={{ opacity: 0, scale: 0.8, y: -50 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                style={{
                  color: "#000",
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: "36px",
                  letterSpacing: "2px",
                  textAlign: "center",
                  margin: 0,
                  fontWeight: "bold",
                  textTransform: "uppercase",
                  textShadow: "2px 2px 4px rgba(0, 0, 0, 0.3)",
                }}
              >
                {t("buttons.popup-details")}
              </motion.h2>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-8 pt-28 mb-5 relative"
              style={{
                border: "3px solid #000000",
                borderRadius: "30px",
                maxWidth: "800px",
                margin: "0 auto",
                backgroundColor: "#fff",
                boxShadow: "0 10px 30px rgba(0, 0, 0, 0.3)",
              }}
            >
              <div className="space-y-6 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-6">
                  {/* Image on top */}
                  <div className="relative">
                    {popupData.media && popupData.media.length > 0 && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                        className="absolute -top-24 left-1/2 transform -translate-x-1/2 z-20"
                      >
                        <div
                          className="relative mx-auto mb-4 rounded-xl overflow-hidden shadow-2xl border-4 border-white hover:shadow-[0_20px_50px_rgba(0,0,0,0.3)] transition-all duration-500"
                          style={{
                            width: "750px",
                            height: "180px",
                            background:
                              "linear-gradient(135deg,rgb(243, 239, 239),rgb(245, 245, 245))",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            border: "4px solid",
                            borderImage:
                              "linear-gradient(45deg, rgb(223, 221, 221), rgb(223, 221, 221)) 1",
                            boxShadow: "0 0 15px rgba(0, 0, 0, 0.3)",
                            borderRadius: "8px",
                          }}
                        >
                          <img
                            src={popupData.media[0]}
                            alt="Popup Media"
                            className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                          />
                        </div>
                      </div>
                    )}
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200 text-center">
                      <h4 className="text-xs font-semibold text-red-800 mb-1 uppercase tracking-wider">
                        {t("fields.title_en")}
                      </h4>{" "}
                      {/* <p className="inline-block bg-gray-100 text-gray-800 text-sm font-semibold px-4 py-1 rounded-full shadow-sm">
                        {popupData.title_en}
                      </p> */}
                      <div
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(popupData.title_en || ""),
                        }}
                        className="prose prose-sm max-w-full text-gray-800 bg-gray-100 p-2 rounded shadow-sm"
                      />
                    </div>
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200 text-center">
                      <h4 className="text-xs font-semibold text-red-800 mb-1 uppercase tracking-wider">
                        {t("fields.title_sp")}
                      </h4>{" "}
                      {/* <p className="inline-block bg-gray-100 text-gray-800 text-sm font-semibold px-4 py-1 rounded-full shadow-sm">
                        {popupData.title_sp}
                      </p> */}
                      <div
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(popupData.title_sp || ""),
                        }}
                        className="prose prose-sm max-w-full text-gray-800 bg-gray-100 p-2 rounded shadow-sm"
                      />
                    </div>

                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200 text-center">
                      <h4 className="text-xs font-semibold text-red-800 mb-1 uppercase tracking-wider">
                        {t("fields.subtitle_en")}
                      </h4>
                      <p className="inline-block bg-gray-100 text-gray-800 text-sm font-semibold px-4 py-1 rounded-full shadow-sm">
                        {popupData.sub_title_en
                          ? popupData.sub_title_en
                          : "No data"}
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200 text-center">
                      <h4 className="text-xs font-semibold text-red-800 mb-1 uppercase tracking-wider">
                        {t("fields.subtitle_sp")}
                      </h4>
                      <p className="inline-block bg-gray-100 text-gray-800 text-sm font-semibold px-4 py-1 rounded-full shadow-sm">
                        {popupData.sub_title_sp
                          ? popupData.sub_title_sp
                          : "No data"}
                      </p>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200 text-center">
                    <h4 className="text-xs font-semibold text-blue-600 mb-1 uppercase tracking-wider">
                      {" "}
                      {t("fields.content_en")}{" "}
                    </h4>

                    {/* <p className="inline-block bg-gray-100 text-gray-800 text-sm font-semibold px-4 py-1 rounded-full shadow-sm">
                      {popupData.body_en}
                    </p> */}
                    <div
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(popupData.body_en || ""),
                      }}
                      className="prose prose-sm max-w-full text-gray-800 bg-gray-100 p-2 rounded shadow-sm"
                    />
                  </div>

                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200 text-center">
                    <h4 className="text-xs font-semibold text-blue-600 mb-1 uppercase tracking-wider">
                      {" "}
                      {t("fields.content_sp")}{" "}
                    </h4>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(popupData.body_sp || ""),
                      }}
                      className="prose prose-sm max-w-full text-gray-800 bg-gray-100 p-2 rounded shadow-sm"
                    />
                    {/* <p className="inline-block bg-gray-100 text-gray-800 text-sm font-semibold px-4 py-1 rounded-full shadow-sm">
                      {popupData.body_sp}
                    </p> */}
                  </div>
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200 text-center">
                    <h4 className="text-xs font-semibold text-blue-600 mb-1 uppercase tracking-wider">
                      {t("fields.link")}
                    </h4>
                    <div className="space-y-2">
                      {Array.isArray(popupData.link) &&
                      popupData.link.length > 0 ? (
                        popupData.link.map((link, index) => (
                          <p
                            key={index}
                            className="inline-block bg-gray-100 text-gray-800 text-sm font-semibold px-4 py-1 rounded-full shadow-sm"
                          >
                            <a
                              href={link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block"
                            >
                              {link}
                            </a>
                          </p>
                        ))
                      ) : (
                        <p className="inline-block bg-gray-100 text-gray-800 text-sm font-semibold px-4 py-1 rounded-full shadow-sm">
                          No link available
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200 text-center">
                    <h4 className="text-xs font-semibold text-blue-600 mb-1 uppercase tracking-wider">
                      {" "}
                      {t("fields.type")}{" "}
                    </h4>
                    <p className="inline-block bg-gray-100 text-gray-800 text-sm font-semibold px-4 py-1 rounded-full shadow-sm">
                      {popupData.type}
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl border border-green-200 text-center">
                    <h4 className="text-xs font-semibold text-green-600 mb-1 uppercase tracking-wider">
                      {" "}
                      {t("fields.button_en")}{" "}
                    </h4>
                    <p className="inline-block bg-gray-100 text-gray-800 text-sm font-semibold px-4 py-1 rounded-full shadow-sm">
                      {popupData.button_en}
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl border border-green-200 text-center">
                    <h4 className="text-xs font-semibold text-green-600 mb-1 uppercase tracking-wider">
                      {" "}
                      {t("fields.button_sp")}{" "}
                    </h4>
                    <p className="inline-block bg-gray-100 text-gray-800 text-sm font-semibold px-4 py-1 rounded-full shadow-sm">
                      {popupData.button_sp}
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-xl border border-purple-200 text-center">
                    <h4 className="text-xs font-semibold text-purple-600 mb-1 uppercase tracking-wider">
                      {" "}
                      {t("fields.createdAt")}
                    </h4>
                    <p className="inline-block bg-gray-100 text-gray-800 text-sm font-semibold px-4 py-1 rounded-full shadow-sm">
                      {new Date(popupData.createdAt).toLocaleDateString(
                        "en-US",
                        {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        }
                      )}
                    </p>
                    <p className="inline-block bg-gray-100 text-gray-800 text-sm font-semibold px-4 py-1 rounded-full shadow-sm">
                      {new Date(popupData.createdAt).toLocaleTimeString(
                        "en-US",
                        {
                          hour: "2-digit",
                          minute: "2-digit",
                        }
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </Container>
        </Box>
      </div>
    </Box>
  );
};

export default ViewPopup;
